import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { getAvatarBackgroundColor } from "./InstanceAvatar";
import { useOrgChannelsQueryStore } from "./OrgChannelsProvider";

export interface OrgChannelSummary {
  id: string;
  displayName: string;
  kind: "private" | "shared";
  memberCount: number;
  ownerActorId: string | null;
  organizationId: string;
  provenance: "issue_workspace" | "manual" | "teams_bind";
  provenanceRef?: string | null;
  deletionStartedAt?: string | null;
  archivedAt?: string | null;
  archivedReason?: string | null;
  createdAt: string;
  deletedAt?: string | null;
  deletedReason?: string | null;
  environmentStatus: string | null;
  activeTeamsBindingId: string | null;
  activeTeamsConversationId: string | null;
  activeTeamsConversationLabel: string | null;
  hasDetachedTeamsBinding: boolean;
  lastActivityAt: string | null;
  teamsBindingChannelKind: "teams_only" | "shared_bound" | null;
  /** Avatar seeds of the actor members in this channel. Used to derive the
   *  channel's accent color via averaging — gives chips an at-a-glance hint
   *  about which template/personality dominates the room. */
  memberAvatarSeeds: string[];
  memberlessSince?: string | null;
  /** Cached averaged accent hex computed from memberAvatarSeeds. Falls back
   *  to a neutral violet when the channel has no actor members. */
  accentColor: string;
  /** Addressed-actor count on the most recent channel message. 0 when no
   *  messages have been fanned out since the recipient ledger started.
   *  Optional only because pre-ledger client builds / fixtures may omit it. */
  addressedCount?: number;
  /** Of the addressed actors, how many claimed their wake. */
  claimedCount?: number;
  /** Seconds since the oldest unclaimed wake recipient in the stranding
   *  window. Null when no stranded wakes are detectable. */
  oldestUnclaimedAge?: number | null;
  /** Channel-message id of the latest wake batch, for deep-linking. */
  lastWakeBatchId?: string | null;
}

interface State {
  channels: OrgChannelSummary[];
  loading: boolean;
  error: string | null;
}

export function useOrgChannels(): State & {
  byId: Map<string, OrgChannelSummary>;
  byName: Map<string, OrgChannelSummary>;
  /** Imperative refetch — for callers that hit a channel id the cached list
   *  doesn't know yet (e.g. navigating to a just-created channel before the
   *  realtime channel_changed reload lands). Resolves true when the list was
   *  actually refreshed; false when the fetch failed (callers must not treat
   *  a failed reload as confirmation that a channel is absent). */
  reload: () => Promise<boolean>;
} {
  const store = useOrgChannelsQueryStore();
  const query = useQuery(store.options, store.client);
  const channels = useMemo(
    () =>
      (query.data?.channels ?? []).map((channel) => ({
        ...channel,
        accentColor: averageAccentColor(channel.memberAvatarSeeds),
      })),
    [query.data],
  );
  const indexes = useMemo(() => buildOrgChannelIndexes(channels), [channels]);
  return {
    channels,
    ...indexes,
    error: query.error?.message ?? null,
    loading: query.isLoading,
    reload: store.reload,
  };
}

export function buildOrgChannelIndexes(
  channels: readonly OrgChannelSummary[],
): {
  byId: Map<string, OrgChannelSummary>;
  byName: Map<string, OrgChannelSummary>;
} {
  const byId = new Map<string, OrgChannelSummary>();
  const byName = new Map<string, OrgChannelSummary>();
  for (const ch of channels) {
    byId.set(ch.id, ch);
    byName.set(ch.displayName, ch);
  }
  return { byId, byName };
}

// ---------------------------------------------------------------------------
// Channel accent color derivation.
//
// Each actor avatar is rendered with one of five palette colors keyed off the
// Avatar seed. Channels feel more identifiable when the chip carries an
// Averaged version of those colors — a room of three Sonnets and one Grok
// Looks emerald-leaning, a room of all Haikus glows amber, etc.
//
// Implementation: RGB-average the member avatar colors, then nudge saturation
// Up so heavily-mixed rooms don't desaturate into mud. Empty rooms fall back
// To neutral violet (the channel-default chrome).
// ---------------------------------------------------------------------------

const FALLBACK_CHANNEL_ACCENT = "#8b5cf6";

function averageAccentColor(seeds: string[]): string {
  if (seeds.length === 0) {
    return FALLBACK_CHANNEL_ACCENT;
  }
  let r = 0;
  let g = 0;
  let b = 0;
  for (const seed of seeds) {
    const hex = getAvatarBackgroundColor(seed);
    const [sr, sg, sb] = hexToRgb(hex);
    r += sr;
    g += sg;
    b += sb;
  }
  const n = seeds.length;
  const avg: [number, number, number] = [
    Math.round(r / n),
    Math.round(g / n),
    Math.round(b / n),
  ];
  const [h, s, l] = rgbToHsl(avg);
  // Boost saturation so muddy averages still read as a color, not grey.
  const boostedS = Math.min(1, s * 1.15 + 0.1);
  return rgbToHex(hslToRgb(h, boostedS, l));
}

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

function clampToHex(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, "0");
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  return `#${clampToHex(r)}${clampToHex(g)}${clampToHex(b)}`;
}

function rgbToHsl([r, g, b]: [number, number, number]): [
  number,
  number,
  number,
] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: {
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
        break;
      }
      case gn: {
        h = ((bn - rn) / d + 2) * 60;
        break;
      }
      default: {
        h = ((rn - gn) / d + 4) * 60;
      }
    }
  }
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const v = l * 255;
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = h / 360;
  const f = (t: number) => {
    let tt = t;
    if (tt < 0) {
      tt += 1;
    }
    if (tt > 1) {
      tt -= 1;
    }
    if (tt < 1 / 6) {
      return p + (q - p) * 6 * tt;
    }
    if (tt < 1 / 2) {
      return q;
    }
    if (tt < 2 / 3) {
      return p + (q - p) * (2 / 3 - tt) * 6;
    }
    return p;
  };
  return [f(hue + 1 / 3) * 255, f(hue) * 255, f(hue - 1 / 3) * 255];
}
